<template>
  <div class="relative al-dropdown">
    <div
      v-show="open === true"
      class="fixed top-0 bottom-0 left-0 right-0 md:hidden overlay z-10"
      @click="open = false"
    />

    <div
      v-if="hasSearch"
    >
      <input
        ref="desktopSearch"
        v-model="search"
        type="text"
        class="w-full h-12 px-3"
        :class="{
          'bright': bright, error: error.length > 0
        }"
        :placeholder="placeholder ? getPlaceholder : 'Search'"
        @focus="focus"
        @mouseup.prevent
        @blur="blurSearchField(true)"
      >
      <label
        class="absolute pointer-events-none floating-label"
        :class="{'has-value': search.length > 0}"
        v-text="placeholder"
      />
      <button
        class="search-arrow"
        @click="toggleDropdown"
      >
        <i
          class="ml-auto fa-solid"
          :class="[open ? 'fa-chevron-up' : 'fa-chevron-down']"
        />
      </button>
    </div>

    <button
      v-else
      type="button"
      class="flex items-center w-full p-3 text-sm text-white cursor-pointer dropdown"
      :class="labelClass"
      :title="label"
      @click="toggleDropdown"
    >
      <span
        class="ellipsis"
        v-html="label"
      />

      <i
        class="ml-auto fa-solid"
        :class="[open ? 'fa-chevron-up' : 'fa-chevron-down']"
      />
    </button>

    <!-- eslint-disable max-len -->
    <div
      v-show="open === true"
      class="fixed bottom-0 left-0 z-10 w-full mt-2 md:absolute md:left-auto md:bottom-auto dropdown-options"
      :class="{
        bright,
        autoscroll: autoScroll,
        'top-0 md:top-auto has-search': hasSearch,
        'block md:hidden': hasSearch && filteredOptions.length === 0
      }"
    >
      <!-- eslint-enable max-len -->
      <div
        v-if="hasSearch"
        class="flex px-5 py-3 fixed top-0 md:hidden z-10 bg-white"
        style="width: calc(100% - 10px); border-bottom: 1px solid #E6E6E6;"
      >
        <button
          @click="closeDropdown"
        >
          <i class="fa-light fa-xmark text-[#b3b3b3]" />
        </button>
        <input
          ref="mobileSearch"
          v-model="search"
          type="text"
          class="ml-5 w-full text-[#333333]"
          style="border: 0; border-radius: 0;"
          :placeholder="placeholder ? getPlaceholder : 'Search'"
        >
      </div>
      <ul
        v-if="filteredOptions.length > 0"
        class="py-2"
        :class="{'mt-12 md:mt-0': hasSearch}"
      >
        <li
          v-for="option in filteredOptions"
          :key="option.slug"
          class="relative px-8 py-3 text-white md:px-4"
          :class="[option.separator ? 'separator' : 'cursor-pointer']"
          @click="setOption(option)"
        >
          <template v-if="option.separator === true">
            <div
              class="text-center my-2 text-[#b2b2b2]"
              style="border-bottom: 1px solid #e6e6e6; line-height: 0;"
            >
              <span
                class="px-2 bg-white text-sm"
                v-text="option.name"
              />
            </div>
          </template>
          <template v-else>
            <span
              v-html="option.name"
            />
            <i
              v-if="(option.slug === selectedOption.slug) || markedOptionsSlugs.includes(option.slug)"
              class="fa-regular fa-check float-right"
            />
          </template>
        </li>
        <!-- eslint-disable max-len -->
        <li
          v-if="!autoScroll || forceCloseBtn"
          class="relative flex items-center px-8 py-3 text-white cursor-pointer md:hidden md:px-4 close"
          @click="toggleDropdown"
        >
          <!-- eslint-enable max-len -->
          Close
          <span />
        </li>
      </ul>
      <ul
        v-else
        class="py-2"
      >
        <li class="relative px-8 py-3 text-white md:px-4">
          No results
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
let mdScreenWidthThreashold = 0;
if (typeof window !== 'undefined') {
  const resolveConfig = require('tailwindcss/resolveConfig');
  const tailwindConfig = require('../../../../tailwind-al.config');
  mdScreenWidthThreashold = parseInt(resolveConfig(tailwindConfig).theme.screens.md.replace('px', ''));
}
export default {
  props: {
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * In case to automatically get option name appended,
     * this prop can be provided. Placeholder provided here
     * will be prepended before option name
     *
     * Remember `placeholder` property will be neglected if used
     * this option.
     */
    prependedPlaceholder: {
      type: String,
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
      required: true,
    },
    markedOptionsSlugs: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number],
      default: '',
    },
    autoScroll: {
      type: Boolean,
      default: false,
    },
    hasSearch: {
      type: Boolean,
      default: false,
    },
    bright: {
      type: Boolean,
      default: false,
    },
    labelClass: {
      type: String,
      default: '',
    },
    forceCloseBtn: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    searchValue: {
      type: String,
      default: '',
    },
    hidePlaceholder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      open: false,
      search: '',
    };
  },
  computed: {
    selectedOption: {
      get() {
        return this.options.find(item => item.slug === this.value) || {};
      },
      set(option) {
        this.open = false;
        this.$emit('input', option.slug, option.name);
      },
    },
    getPlaceholder() {
      return this.hasSearch && this.hidePlaceholder ? '' : this.placeholder;
    },
    label() {
      return this.prependedPlaceholder
        ? `${this.prependedPlaceholder} ${this.selectedOption.name}`
        : this.placeholder;
    },
    filteredOptions() {
      const searchVal = this.search.toLowerCase();
      if (this.hasSearch && searchVal) {
        const filteredOptions = this.options.filter(item => item.name.toLowerCase().includes(searchVal) && item.separator !== true);
        if (filteredOptions.length === 1 && searchVal === this.selectedOption.name.toLowerCase()) {
          return this.options;
        }

        return filteredOptions;
      }

      return this.options;
    },
  },

  watch: {
    selectedOption() {
      this.search = this.label.trim() !== 'undefined' ? this.label.trim() : '';
    },
    open() {
      const eventName = this.open ? 'open' : 'close';
      this.$emit(eventName);
      if (this.screenEqualOrLessThanMd()) {
        if (this.open) {
          document.body.classList.add('overflow-hidden');
        } else {
          document.body.classList.remove('overflow-hidden');
        }
      }
    },
  },

  mounted() {
    document.body.addEventListener('click', this.clickDom);
    this.search = this.searchValue || '';
  },

  beforeDestroy() {
    document.body.removeEventListener('click', this.clickDom);
  },

  methods: {
    screenEqualOrLessThanMd() {
      return window.innerWidth <= mdScreenWidthThreashold;
    },
    setOption(option) {
      if (option.separator !== true) {
        this.selectedOption = option;
      }
    },

    focus() {
      this.openDropdown();
      this.$nextTick(() => {
        if (this.screenEqualOrLessThanMd() && this.$refs.mobileSearch) {
          this.$refs.mobileSearch.focus();
          this.$refs.mobileSearch.select();
        }
      });
    },

    openDropdown() {
      this.open = true;
      if (this.hasSearch && this.screenEqualOrLessThanMd()) {
        this.$refs.desktopSearch.blur();
      } else if (this.$refs.desktopSearch) {
        this.$refs.desktopSearch.select();
      }
    },

    closeDropdown() {
      this.open = false;
      // Programatically call blur action when dropdown is closed
      if (this.$refs.mobileSearch) {
        this.blurSearchField(false);
      }
    },

    toggleDropdown() {
      if (this.open) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    },

    clickDom(e) {
      if (!this.$el.contains(e.target)) {
        this.open = false;
      }
    },

    blurSearchField(desktop) {
      if (
        this.filteredOptions.length === 1
        && this.filteredOptions[0].name.toLowerCase() === this.search.toLowerCase()
      ) {
        this.selectedOption = this.filteredOptions[0];
      } else {
        if (this.hasSearch && desktop && this.screenEqualOrLessThanMd()) {
          return;
        }
        // Delay a bit as this blur call could be because of selecting option
        // from dropdown. Let that event get triggered before we move to remaining
        // part of this event where we have to revert back the search value
        setTimeout(() => {
          this.search = this.selectedOption.name || '';
        }, 200);
      }
    },
  },
};
</script>

<style scoped>
.text-\[\#666666\] {
  color: #666666;
}
.text-\[\#b3b3b3\] {
  color: #b3b3b3;
}
.text-\[\#534cda\] {
  color: #534cda;
}
.text-\[\#333333\] {
  color: #333333;
}
.text-\[\#b2b2b2\] {
  color: #b2b2b2;
}
.bg-\[\#f2f2f2\] {
  background-color: #f2f2f2;
}
.dropdown {
  border: 1px solid #666666;
  border-radius: 10px;
}
.dropdown-options {
  background-color: #4d4d4d;
  border-radius: 20px 20px 0 0;
  transition: all 0.3s;
  box-shadow: rgb(0 0 0 / 30%) 0px 3px 10px;
}
.dropdown-options.autoscroll {
  max-height: 300px;
  overflow-y: auto;
}
.dropdown-options.has-search {
  max-height: none;
  height: 100vh;
  border-radius: 0;
  z-index: 9999;
  margin-top: 0;
}
.dropdown-options.bright {
  @apply bg-white;
}
.dropdown-options.bright li {
  @apply text-[#333333];
}
.dropdown-options.bright li:not(.separator):hover {
  @apply bg-[#f2f2f2];
}
/* Chrome, Edge, and Safari */
.autoscroll::-webkit-scrollbar-button {
  display: none;
}
.autoscroll::-webkit-scrollbar {
  width: 10px;
}
.autoscroll::-webkit-scrollbar-track {
  border-radius: 50px;
  background: transparent;
  margin-top: 8px;
  margin-bottom: 8px;
}
.autoscroll::-webkit-scrollbar-thumb {
  background-color: #232323;
  border-radius: 16px;
  border: 2px solid #373737;
}

li {
  transition: all 0.3s;
}
li:not(.separator):not(.separator):hover {
  background-color: #666666;
}
li.active:after {
  content: "";
  position: absolute;
  right: 32px;
  top: calc(50% - 8px);
  height: 16px;
}
.overlay {
  background-color: rgba(0, 0, 0, 0.6);
}
.close {
  color: #b2b2b2;
}
.close span {
  position: absolute;
  right: 32px;
  top: calc(50% - 8px);
  width: 16px;
  height: 16px;
}
.close span:before,
.close span:after {
  position: absolute;
  left: 16px;
  content: " ";
  height: 16px;
  width: 1px;
  background-color: #b2b2b2;
}
.close span:before {
  transform: rotate(45deg);
}
.close span:after {
  transform: rotate(-45deg);
}
.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
input {
  @apply text-[#b3b3b3];
  border: 1px solid #B3B3B3;
  border-radius: 10px;
}
input.bright {
  color: inherit;
}
input::selection {
  background-color: rgba(130, 123, 255, 0.15);
}
.search-arrow {
  @apply absolute;
  top: 12px;
  right: 20px;
}
.search-arrow i {
  @apply text-[#666666];
}
.floating-label {
  @apply text-[#b3b3b3];
  left: 12px;
  top: calc(50% - 12px);
  transition: all 0.3s;
}
input:focus + .floating-label,
input.value + .floating-label,
.floating-label.has-value {
  @apply text-[#534cda];
  left: 0px;
  transform: scale(0.75) translateY(-32px);
  background-color: white;
  padding-left: 6px;
  padding-right: 6px;
}
input:focus {
  border-color: #534CDA;
}
input.error {
  border-color: #ee5951;
}
input.error::placeholder,
input.error + .floating-label {
  color: #ee5951;
}
@screen md {
  .dropdown-options {
    border-radius: 10px !important;
  }
  .dropdown-options.has-search {
    max-height: 300px;
    height: auto;
    z-index: 10;
    margin-top: 8px;
  }
  li.active:after {
    right: 16px;
  }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active  {
    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
    box-shadow: 0 0 0px 1000px transparent inset !important;
    transition: background-color 5000s ease-in-out 0s;
    -webkit-text-fill-color: #B3B3B3 !important;
}
</style>
